/**
 * Targeted partial analysis updates — maintenance recommendations and market valuations.
 * These are cheaper/faster than a full analysis since they focus on one section.
 */
import { aiCall } from './aiProxy';
import { CREDIT_COSTS } from '../utils/credits';
import {
  WEB_SEARCH_TOOL,
  MAX_TOOL_ROUNDS,
  formatServiceLog,
  buildVehicleContext,
} from './analysisUtils';

const DEV = import.meta.env.DEV;

/* ───────────────────── Maintenance Update ───────────────────── */

const MAINTENANCE_SYSTEM_PROMPT = `You are Pocket Mechanic, an AI automotive expert. Update the maintenance recommendations for this vehicle based on its current data, mileage, condition, and service history.

Do NOT provide market valuations or pricing. Focus ONLY on:
1. What maintenance is due or coming due
2. Health score assessment
3. Brief health summary

Adapt language to the owner's skill level (Beginner=safety-focused, Expert=peer-level technical). Factor in ZIP code for regional labor rates.

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "health_score": number,
  "maintenance_due": [
    {
      "service": string,
      "urgency": "high"|"medium"|"low",
      "cost_dealer": string,
      "cost_shop": string,
      "cost_diy": string,
      "difficulty": "Beginner"|"Intermediate"|"Advanced"|"Expert",
      "time_estimate": string,
      "why_important": string,
      "warning": string
    }
  ],
  "summary": string
}

HEALTH SCORE GUIDANCE (0-100):
The health score reflects the vehicle's CURRENT MECHANICAL HEALTH — not its age or mileage.

Primary factor (~60% weight): MAINTENANCE COMPLIANCE
- Is the vehicle up to date on scheduled maintenance?
- Does the service history show consistent, proactive care?
- Are there critical deferred items (timing belt, fluids, brakes)?
- A vehicle with a complete, up-to-date service history should start at 85+ regardless of age or mileage.

Secondary factor (~25% weight): OUTSTANDING ISSUES
- How many high-urgency maintenance items are currently due?
- Deduct for each overdue critical service, scaled by urgency.
- A vehicle with zero high-urgency items and only a few medium items should score 80+.

Tertiary factor (~15% weight): AGE & MILEAGE RISK
- Higher mileage and age increase the probability of wear-related failures, but this is a MINOR modifier — not a primary driver.
- A well-maintained 200,000-mile vehicle should score 75-90. A neglected 30,000-mile vehicle with multiple overdue services should score 40-60.
- For classics and collector vehicles (20+ years old), age is essentially irrelevant to health — only maintenance history and current condition matter.

IMPORTANT: Never score below 70 solely because of high mileage or old age when the service history is strong and no critical items are overdue.`;

/**
 * Run a targeted maintenance recommendation update.
 * Returns { maintenanceItems, healthScore, healthSummary } or throws.
 */
export async function updateMaintenanceRecommendations(vehicle, serviceLog, signal) {
  if (DEV) console.log('[PartialAnalysis] Starting maintenance update for:', `${vehicle.year} ${vehicle.make} ${vehicle.model}`);

  const { conditionGuidance, serviceLogText, profileContext, tmuContext } = buildVehicleContext(vehicle, serviceLog);

  const userPrompt = `Update the maintenance recommendations for this vehicle based on all available data.

Year: ${vehicle.year}
Make: ${vehicle.make}
Model: ${vehicle.model}
Current Odometer: ${vehicle.mileage} miles${vehicle.tmu ? ' (TMU — Total Mileage Unknown)' : ''}${vehicle.vin ? `\nVIN: ${vehicle.vin}` : ''}
Last Service Date: ${vehicle.lastService || 'Unknown'}
Owner Skill Level: ${vehicle.skillLevel}
Primary Use: ${Array.isArray(vehicle.primaryUse) ? vehicle.primaryUse.join(', ') : vehicle.primaryUse}${vehicle.serviceHistory ? `\nService History Notes: ${vehicle.serviceHistory}` : ''}${tmuContext}${conditionGuidance}${profileContext}${serviceLogText}

Based on the vehicle's current mileage, condition, and completed service history, what maintenance is recommended next? Do NOT recommend services already in the service log.`;

  const { result } = await aiCall({
    action: 'chat_simple',
    creditCost: CREDIT_COSTS.maintenanceUpdate,
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: MAINTENANCE_SYSTEM_PROMPT },
      { role: 'user', content: userPrompt },
    ],
    temperature: 0.4,
    max_tokens: 1500,
  }, { signal, timeout: 30000 });

  const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  const parsed = JSON.parse(cleaned);

  const maintenanceItems = (parsed.maintenance_due || []).map((item) => ({
    service: item.service,
    urgency: item.urgency,
    description: item.why_important || '',
    costDealer: item.cost_dealer,
    costShop: item.cost_shop,
    costDIY: item.cost_diy,
    difficulty: item.difficulty,
    timeEstimate: item.time_estimate,
    warning: item.warning,
  }));

  if (DEV) console.log('[PartialAnalysis] Maintenance update complete:', maintenanceItems.length, 'items');

  return {
    maintenanceItems,
    healthScore: parsed.health_score,
    healthSummary: parsed.summary,
  };
}

/* ───────────────────── Valuation Update ───────────────────── */

const VALUATION_SYSTEM_PROMPT = `You are Pocket Mechanic, an AI automotive expert. Provide a FRESH market valuation for this vehicle.

Use web_search to find recent actual sales from: Bring a Trailer, Cars & Bids, Hemmings, eBay Motors sold listings. NEVER use KBB/NADA/Edmunds for collector, enthusiast, exotic, or modified vehicles. Search for completed auctions from the past 6-12 months. Run 3-4 varied searches targeting different sources.

If searches fail, set recent_sales to [] and note "Unable to find recent comparable sales data" in data_source. Do NOT fabricate data.

Do NOT provide maintenance recommendations. Focus ONLY on market value.

MODEL-SPECIFIC VALUE ADJUSTMENTS:
Before providing your valuation, identify 3-5 known common issues or high-value services specific to this exact model (e.g., timing belt on interference engines, IMS bearing on Porsche, head gaskets on Subaru EJ engines, VANOS on BMW). For each:
- If the service log shows it HAS been completed: this is a value-add. Raise the FLOOR of the value range (buyers pay more for a sorted example).
- If the service log shows it has NOT been done (or no service log exists): this represents deferred risk. Lower the FLOOR of the value range accordingly.
- Trim-specific premiums (e.g., GTS vs base) should affect the CEILING of the range.

Respond ONLY with valid JSON (no markdown, no code fences). Structure:
{
  "market_value": {
    "current_value_range": string,
    "value_trend": string,
    "recent_sales": [string],
    "value_factors": string,
    "data_source": string,
    "service_impact": [
      {
        "service": string,
        "status": "completed" | "not_found" | "unknown",
        "impact": string
      }
    ]
  }
}`;

/**
 * Run a targeted market valuation update with web search.
 * Returns { marketValue } or throws.
 */
export async function updateMarketValuation(vehicle, serviceLog, signal) {
  if (DEV) console.log('[PartialAnalysis] Starting valuation update for:', `${vehicle.year} ${vehicle.make} ${vehicle.model}`);

  const { conditionGuidance, serviceLogText } = buildVehicleContext(vehicle, serviceLog || []);

  const userPrompt = `Provide a fresh market valuation for this vehicle.

Year: ${vehicle.year}
Make: ${vehicle.make}
Model: ${vehicle.model}
${vehicle.trim ? `Trim: ${vehicle.trim}` : ''}
Current Odometer: ${vehicle.mileage} miles${vehicle.tmu ? ' (TMU — Total Mileage Unknown)' : ''}${conditionGuidance}${serviceLogText}

Use web_search to find recent comparable sales before providing the valuation. Identify model-specific known issues and cross-reference with the service log to assess their impact on value.`;

  // Multi-round tool loop handled by server
  const { result } = await aiCall({
    action: 'chat_tools',
    creditCost: CREDIT_COSTS.valuationUpdate,
    model: 'gpt-4o-mini',
    messages: [
      { role: 'system', content: VALUATION_SYSTEM_PROMPT },
      { role: 'user', content: userPrompt },
    ],
    tools: [WEB_SEARCH_TOOL],
    temperature: 0.4,
    max_tokens: 1200,
    maxToolRounds: MAX_TOOL_ROUNDS,
  }, { signal, timeout: 120000 }); // Longer timeout — server does multiple rounds

  const cleaned = result.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
  const parsed = JSON.parse(cleaned);
  const mv = parsed.market_value || {};

  const marketValue = {
    estimate: mv.current_value_range || 'N/A',
    trend: mv.value_trend?.toLowerCase().includes('appreciat')
      ? 'appreciating'
      : mv.value_trend?.toLowerCase().includes('declin')
        ? 'declining'
        : 'stable',
    trendNote: mv.value_trend || '',
    recentSales: mv.recent_sales || [],
    valueFactors: mv.value_factors || '',
    dataSource: mv.data_source || '',
    serviceImpact: mv.service_impact || [],
  };

  if (DEV) console.log('[PartialAnalysis] Valuation update complete:', marketValue.estimate);
  return { marketValue };
}
